import type { Entity } from '@/types/entity';
import type { PlotColumn, Project } from '@/types/project';
import type { AssertionCandidate } from './canonBundle';
import { getAssertionCandidates } from './canonBundle';

/**
 * K5-e 트랙 합류 — 작가 레포 트랙(canon/manuscript.beats.yaml: 전제 포함 비트 + 그 부의 사실)을
 * Dexie 정본으로 들여오는 계획을 짠다(순수 함수 — 커밋은 store가 한 번에).
 * 주권: AI 산출 신규 확정이 아니라 작가가 레포에 저술·커밋해온 정본의 *이주* — 단 실행은 작가의 확인 클릭.
 * 봉인: 새 시간축 칼럼의 order(봉인 ordinal)는 작가가 고른 삽입 위치에서 나온다(자동 추측 0).
 */
export interface RepoAssertDto {
  claim: string;
  referencesFact?: string | null;
  referencesEntity?: string | null;
}
export interface RepoBeatDto {
  id: string;
  title: string;
  body: string;
  refs: string[];
  asserts: RepoAssertDto[];
}
export interface RepoTrackDto {
  sequence: string;
  timeScope: string;
  label: string;
  lawless: boolean;
  facts: string[];
  beats: RepoBeatDto[];
}

export interface ImportPlan {
  plotColumns: PlotColumn[];
  /** project.meta.engine 전체(타 키 보존, timeScopes만 합집합 갱신) */
  engineMeta: Record<string, unknown>;
  beats: Entity[];
  notes: string[];
  summary: { beats: number; asserts: number; facts: number; resolvedRefs: number; unresolved: string[] };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

/** 편집거리 ≤1 — 레포 트랙과 앱의 표기 차(rasia↔lasia)를 콘텐츠 하드코딩 없이 잇는 일반 규칙. */
export function editDistanceLe1(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i] && (diff += 1) > 1) return false;
    return true;
  }
  const [s1, s2] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < s1.length) {
    if (s1[i] === s2[j]) {
      i += 1;
      j += 1;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    j += 1;
  }
  return true;
}

const norm = (s: string) => s.toLowerCase().replace(/^(fac|res|loc|chr)-/, '');

/** 레포 ref(영문 슬러그) → 앱 entity id 해소: 정확 id → 정확 이름 → 접두사 제거 동치 → 편집거리 1. */
export function buildEntityResolver(entities: Entity[]): (ref: string) => string | null {
  const pool = entities.filter((e) => e.type !== 'beat' && !e.deletedAt);
  return (ref: string) => {
    const q = ref.trim();
    if (!q) return null;
    const exact = pool.find((e) => e.id === q) ?? pool.find((e) => e.name === q);
    if (exact) return exact.id;
    const nq = norm(q);
    const normed = pool.find((e) => norm(e.id) === nq) ?? pool.find((e) => editDistanceLe1(norm(e.id), nq));
    return normed ? normed.id : null;
  };
}

export function planRepoImport(
  dto: RepoTrackDto,
  state: { project: Project; entities: Entity[] },
  placement: { afterColumnId: string },
  nowTs: number,
): ImportPlan {
  const notes: string[] = [];
  const scopeId = dto.timeScope;
  const byId = new Map(state.entities.map((e) => [e.id, e]));
  const incomingBeatIds = new Set<string>();
  for (const beat of dto.beats) {
    if (incomingBeatIds.has(beat.id)) throw new Error(`중복 비트 id: ${beat.id}`);
    incomingBeatIds.add(beat.id);
  }

  // 1) 시간축 칼럼 — 없으면 작가가 고른 칼럼 *뒤*에(봉인 ordinal = 그 사이 중간값).
  const sorted = state.project.plotColumns.slice().sort((a, b) => a.order - b.order);
  let plotColumns = state.project.plotColumns;
  if (!sorted.some((c) => c.id === scopeId)) {
    const at = sorted.findIndex((c) => c.id === placement.afterColumnId);
    if (at === -1) throw new Error(`삽입 위치 칼럼 없음: ${placement.afterColumnId}`);
    const prev = sorted[at];
    const next = sorted[at + 1];
    const order = next ? (prev.order + next.order) / 2 : prev.order + 1;
    plotColumns = [...state.project.plotColumns, { id: scopeId, label: dto.label, order }];
    notes.push(`+ 시간축 «${scopeId}» (${dto.label}) — ${prev.id} 뒤, 봉인 ordinal ${order}`);
  } else {
    notes.push(`= 시간축 «${scopeId}» 이미 있음 — 사실만 합집합`);
  }

  // 2) 그 부의 사실 합집합(meta.engine.timeScopes — 타 키·타 scope 보존)
  const engineRaw = isRecord(state.project.meta.engine) ? state.project.meta.engine : {};
  const tsRaw = isRecord(engineRaw.timeScopes) ? engineRaw.timeScopes : {};
  const scopeMeta = isRecord(tsRaw[scopeId]) ? (tsRaw[scopeId] as Record<string, unknown>) : {};
  const existingFacts = Array.isArray(scopeMeta.facts)
    ? (scopeMeta.facts as unknown[]).filter((f): f is string => typeof f === 'string')
    : [];
  const mergedFacts = [...existingFacts, ...dto.facts.filter((f) => !existingFacts.includes(f))];
  const engineMeta = {
    ...engineRaw,
    timeScopes: {
      ...tsRaw,
      [scopeId]: { ...scopeMeta, facts: mergedFacts, ...(dto.lawless ? { lawless: true } : {}) },
    },
  };

  // 3) 비트 — 같은 id가 있으면 병합(전제 claim 중복 제거, bodyDraft는 비었을 때만), 없으면 신규.
  const resolveEntity = buildEntityResolver(state.entities);
  let assertCount = 0;
  let resolvedRefs = 0;
  const unresolved = new Set<string>();
  const beats: Entity[] = dto.beats.map((rb, idx) => {
    const existing = byId.get(rb.id);
    if (existing && existing.type !== 'beat') {
      throw new Error(`비트 id 충돌: ${rb.id}는 기존 ${existing.type} 엔티티다`);
    }
    const refs = rb.refs.map((r) => {
      const hit = resolveEntity(r);
      if (hit) {
        resolvedRefs += 1;
        return hit;
      }
      unresolved.add(r);
      return r;
    });
    const candidates: AssertionCandidate[] = rb.asserts.map((a, ai) => {
      const resolvedAssertionEntity = a.referencesEntity ? resolveEntity(a.referencesEntity) : null;
      if (resolvedAssertionEntity) resolvedRefs += 1;
      else if (a.referencesEntity) unresolved.add(a.referencesEntity);
      return {
        id: `imp-${rb.id}-${ai}`,
        beatId: rb.id,
        claim: a.claim,
        ...(a.referencesFact ? { references_fact: a.referencesFact } : {}),
        ...(a.referencesEntity ? { references_entity: resolvedAssertionEntity ?? a.referencesEntity } : {}),
        status: 'accepted',
        source: 'manual',
        confidence: 1,
        rationale: `레포 트랙 합류(${dto.sequence})`,
        evidence: { entityIds: refs.filter((r) => byId.has(r) || resolveEntity(r)), canonRefs: [] },
        createdAt: nowTs,
      };
    });
    assertCount += candidates.length;

    if (existing) {
      const prior = getAssertionCandidates(existing);
      const fresh = candidates.filter((c) => !prior.some((p) => p.claim === c.claim));
      const meta = isRecord(existing.meta) ? existing.meta : {};
      const engine = isRecord(meta.engine) ? meta.engine : {};
      const hasBody = String(existing.fields.bodyDraft ?? '').trim().length > 0;
      const priorRefs = existing.refs ?? [];
      const mergedRefs = [...priorRefs, ...refs.filter((r) => !priorRefs.includes(r))];
      if (fresh.length || !hasBody) notes.push(`± 비트 «${rb.id}» 병합 — 전제 +${fresh.length}${hasBody ? '' : ' · 본문 채움'}`);
      return {
        ...existing,
        version: existing.version + 1,
        updatedAt: nowTs,
        refs: mergedRefs,
        fields: { ...existing.fields, ...(hasBody ? {} : { bodyDraft: rb.body }) },
        meta: { ...meta, engine: { ...engine, assertions: [...prior, ...fresh] } },
      } as Entity;
    }
    return {
      id: rb.id,
      type: 'beat',
      name: rb.title || rb.id,
      summary: '',
      fields: { col: scopeId, order: idx, bodyDraft: rb.body, summary: '', pov: '' },
      refs,
      meta: { engine: { assertions: candidates } },
      canonStatus: 'draft',
      confidence: 'probable',
      version: 1,
      createdAt: nowTs,
      updatedAt: nowTs,
    } as Entity;
  });

  if (unresolved.size) notes.push(`! 미해소 참조 ${unresolved.size}종: ${[...unresolved].join(', ')} — 검사에서 정직하게 드러난다`);

  return {
    plotColumns,
    engineMeta,
    beats,
    notes,
    summary: {
      beats: beats.length,
      asserts: assertCount,
      facts: mergedFacts.length,
      resolvedRefs,
      unresolved: [...unresolved],
    },
  };
}
